<template>
  <div class="d-flex flex-column">
    <div class="text-center">
      <a
        :href="appUrl"
        target="_blank"
        ><img
          alt="app-logo"
          height="150px"
          :src="appLogo"
      /></a>
    </div>

    <div>
      <h1 class="mt-0">
        {{ $t("views.signup_in.signup_title") }}
      </h1>
      <p>
        <span
          class="h4 font-weight-light my-5"
          v-html="$t('views.signup_in.signup_subtitle')"
        ></span>
        <span>
          <router-link
            to="/signin"
            class="text-decoration-underline ml-3"
            text
          >
            {{ $t("views.signup_in.signin") }}
          </router-link>
        </span>
      </p>
    </div>

    <v-form
      ref="form"
      v-model="isFormValid"
      class="mt-5"
    >
      <v-text-field
        v-if="!hasBeenInvited"
        :label="$t('categories.company.name')"
        v-model="signupForm.company"
        :rules="rules.company"
      />

      <v-text-field
        v-model="signupForm.email"
        :disabled="hasBeenInvited"
        :label="$t('commons.email')"
        :rules="rules.email"
        required
      />

      <div class="d-flex gap-4">
        <v-text-field
          :label="$t('commons.first_name')"
          :rules="rules.first_name"
          v-model="signupForm.first_name"
          required
          @input="updateForm"
        />

        <v-text-field
          :label="$t('commons.last_name')"
          :rules="rules.last_name"
          v-model="signupForm.last_name"
          required
          @input="updateForm"
        />
      </div>

      <v-text-field
        clearable
        :append-icon="showPassword.password1 ? 'mdi-eye-off' : 'mdi-eye'"
        @click:append="showPassword.password1 = !showPassword.password1"
        :type="showPassword.password1 ? 'text' : 'password'"
        :rules="rules.password1"
        v-model="signupForm.password1"
        :label="$t('commons.pwd')"
        @input="updateForm"
      />

      <v-text-field
        clearable
        :append-icon="showPassword.password2 ? 'mdi-eye-off' : 'mdi-eye'"
        @click:append="showPassword.password2 = !showPassword.password2"
        :type="showPassword.password2 ? 'text' : 'password'"
        :rules="rules.password2"
        v-model="signupForm.password2"
        :label="$t('views.personal_settings.repeat_pwd')"
        @input="updateForm"
      />

      <v-checkbox
        :rules="rules.termsAndPrivacy"
        v-model="signupForm.termsAndPrivacy"
        :label="$t('messages.information.terms_inspecto')"
      ></v-checkbox>

      <v-btn
        large
        :loading="loading"
        :disabled="!isFormValid || loading"
        color="blue white--text"
        block
        @click="submit"
      >
        {{ $t("views.signup_in.signup") }}
      </v-btn>
    </v-form>
  </div>
</template>

<script>
import { required, email, password } from "@/utils/validator"
import { AccountService } from "@/services"

export default {
  data() {
    return {
      appLogo: this.$appConfig.assets.img.custom.logo,
      appUrl: this.$appConfig.url,
      isFormValid: false,
      rules: {
        email: [required, email],
        company: [required],
        first_name: [
          required,
          v => !!v || this.$t("messages.validator.field_required"),
          // v => /^[\p{L} \s-]*$/.test(v) || this.$t("messages.validator.name_regex")
          v =>
            /^[a-zA-Z\s-àáâäãåçèéêëìíîïñòóôöõùúûüýÿÆæØøÅå]*$/.test(v) ||
            this.$t("messages.validator.name_regex")
        ],
        last_name: [
          required,
          v => !!v || this.$t("messages.validator.field_required"),
          // v => /^[\p{L} \s-]*$/.test(v) || this.$t("messages.validator.name_regex")
          v =>
            /^[a-zA-Z\s-àáâäãåçèéêëìíîïñòóôöõùúûüýÿÆæØøÅå]*$/.test(v) ||
            this.$t("messages.validator.name_regex")
        ],
        password1: [required, password],
        password2: [
          required,
          v =>
            v === this.signupForm.password1 || this.$t("views.invalid_invitation.repeat_same_pwd")
        ],
        termsAndPrivacy: [
          required,
          v => v === true || this.$t("views.invalid_invitation.accept_term")
        ]
      },
      signupForm: {
        company: "",
        email: "",
        first_name: "",
        last_name: "",
        password1: "",
        password2: "",
        invite_key: this.inviteKey,
        termsAndPrivacy: false
      },
      showPassword: {
        password1: false,
        password2: false
      },
      loading: false
    }
  },
  props: {
    invitation: {
      type: Object,
      default: () => ({})
    },
    inviteKey: {
      type: String,
      default: ""
    }
  },
  computed: {
    hasBeenInvited() {
      return !!this.inviteKey
    },
    canSubmit() {
      return this.signupForm.$invalid || this.loading
    }
  },
  methods: {
    validatePWD() {
      if (this.signupForm.password1 && this.signupForm.password2) {
        if (this.signupForm.password1 === this.signupForm.password2) {
          this.rules.password2 = [true, true, true]
          return true
        } else {
          this.rules.password2 = [true, true, this.$t("views.invalid_invitation.repeat_same_pwd")]
          return
        }
      }
    },
    reset() {
      this.$refs.form.reset()
      this.$refs.form.resetValidation()
    },
    submit() {
      if (this.loading) return
      this.$refs.form.validate()
      this.$nextTick(() => {
        if (this.isFormValid) {
          this.loading = true
          AccountService.register(this, {
            ...this.signupForm,
            ...(this.signupForm.termsAndPrivacy ? { terms: true, privacy: true } : {})
          })
            .then(result => {
              if (this.hasBeenInvited) {
                this.$router.push({ name: "SignIn" })
              } else {
                this.$store.commit("app/success", result.data.detail)
                this.$router.push({ name: "Sign up Activation" })
              }
            })
            .finally(() => {
              this.loading = false
            })
        }
      })
    },
    updateForm() {
      if (this.validatePWD()) {
        this.$emit("input", this.signupForm)
      }
    }
  },
  mounted() {
    this.$refs.form.reset()
  },
  watch: {
    invitation: {
      immediate: true,
      handler(newValue, oldValue) {
        this.signupForm = { ...this.signupForm, ...newValue }
      }
    }
  }
}
</script>

<style scoped>
.gap-4 {
  gap: 16px;
}
</style>
